import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Save, MapPin } from 'lucide-react';
import PageHeader from '../../components/admin/PageHeader';
import ContentCard from '../../components/admin/ContentCard';
import LoadingState from '../../components/admin/LoadingState';
import toast from 'react-hot-toast';

const LocationForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;
  
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [location, setLocation] = useState({
    name: '',
    type: 'station',
    address: '',
    coordinates: {
      x: 0,
      y: 0
    }
  });

  useEffect(() => {
    document.title = isEditing ? 'Modifier un Emplacement | Admin' : 'Ajouter un Emplacement | Admin';
    
    if (isEditing) {
      loadLocation();
    }
  }, [isEditing, id]);

  const loadLocation = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      
      if (data) {
        setLocation(data);
      }
    } catch (error) {
      console.error('Error loading location:', error);
      toast.error('Erreur lors du chargement de l\'emplacement');
      navigate('/admin/locations');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setLocation({ ...location, [name]: value });
  };

  const handleCoordinateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const coord = name === 'latitude' ? 'y' : 'x';
    setLocation({
      ...location,
      coordinates: {
        ...location.coordinates,
        [coord]: parseFloat(value) || 0
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      // Validate required fields
      if (!location.name || !location.type || !location.address) {
        toast.error('Veuillez remplir tous les champs obligatoires');
        return;
      }
      
      let response;
      
      if (isEditing) {
        // Update existing location
        response = await supabase
          .from('locations')
          .update({
            name: location.name,
            type: location.type,
            address: location.address,
            coordinates: `(${location.coordinates.x},${location.coordinates.y})`
          })
          .eq('id', id);
      } else {
        // Insert new location
        response = await supabase
          .from('locations')
          .insert([{
            name: location.name,
            type: location.type,
            address: location.address,
            coordinates: `(${location.coordinates.x},${location.coordinates.y})`
          }]);
      }
      
      if (response.error) throw response.error;
      
      toast.success(isEditing ? 'Emplacement mis à jour avec succès' : 'Emplacement ajouté avec succès');
      navigate('/admin/locations');
    } catch (error) {
      console.error('Error saving location:', error);
      toast.error('Erreur lors de l\'enregistrement de l\'emplacement');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="px-6">
        <PageHeader
          title={isEditing ? 'Modifier l\'Emplacement' : 'Ajouter un Emplacement'}
          backLink="/admin/locations"
        />
        <LoadingState type="form" />
      </div>
    );
  }

  return (
    <div className="px-6">
      <PageHeader
        title={isEditing ? 'Modifier l\'Emplacement' : 'Ajouter un Emplacement'}
        backLink="/admin/locations"
      />
      
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="lg:col-span-2">
            <ContentCard title="Informations de l'Emplacement">
              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Nom *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={location.name}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
                    Type *
                  </label>
                  <select
                    id="type"
                    name="type"
                    value={location.type}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                  >
                    <option value="store">Magasin</option>
                    <option value="station">Station de recharge</option>
                    <option value="partner">Partenaire</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                    Adresse *
                  </label>
                  <textarea
                    id="address"
                    name="address"
                    value={location.address}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                </div>
              </div>
            </ContentCard>
          </div>
          
          <div>
            <ContentCard title="Coordonnées Géographiques">
              <div className="space-y-6">
                <div>
                  <label htmlFor="latitude" className="block text-sm font-medium text-gray-700 mb-1">
                    Latitude
                  </label>
                  <input
                    type="number"
                    id="latitude"
                    name="latitude"
                    value={location.coordinates.y}
                    onChange={handleCoordinateChange}
                    step="0.000001"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                
                <div>
                  <label htmlFor="longitude" className="block text-sm font-medium text-gray-700 mb-1">
                    Longitude
                  </label>
                  <input
                    type="number"
                    id="longitude"
                    name="longitude"
                    value={location.coordinates.x}
                    onChange={handleCoordinateChange}
                    step="0.000001"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                
                <div className="p-4 bg-gray-50 rounded-md">
                  <p className="text-sm text-gray-600">
                    Vous pouvez obtenir les coordonnées en cherchant l'adresse sur Google Maps, puis en faisant un clic droit sur le point et en sélectionnant "Plus d'infos sur cet endroit".
                  </p>
                </div>
              </div>
            </ContentCard>
          </div>
        </div>
        
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/admin/locations')}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center"
          >
            {saving ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Enregistrement...
              </>
            ) : (
              <>
                <Save size={16} className="mr-2" />
                {isEditing ? 'Mettre à jour' : 'Enregistrer'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LocationForm;